<template>
    <div class="w-full space-y-3">
        <Head title="Reports"/>
        <div class="flex justify-between">
            <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">Invoice Status Report (Suppliers)</h1>
            <a :href="route('frontend.reports.invoice_status_report_suppliers_export', {
                _query: {
                    starts_at: form.starts_at,
                    ends_at: form.ends_at,
                    manager: form.manager,
                    supplier: form.supplier,
                    client: form.client,
                    trading_entity: form.trading_entity
                }})" class="py-3 px-3 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amaze-coral w-40 text-center">Download</a>
        </div>
    </div>
    <br />
    <div class="grid grid-cols-3 gap-2">
        <div class="relative" v-if="trading_details.length > 1">
            <label for="trading-entity" class="block text-sm font-medium text-gray-700">Trading Entity</label>
            <select id="trading-entity" v-model="form.trading_entity"
                class="mt-2 bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
                <option value="" v-html="'All'"></option>
                <option v-for="(trading_detail, index) in trading_details" :key="index" :value="trading_detail.id" :selected="form.trading_entity === trading_detail.id" v-html="trading_detail.trading_name"></option>
            </select>
        </div>
        <div class="relative">
            <label for="start-date" class="block text-sm font-medium text-gray-700">Start Date <span v-if="formattedStartDate">({{ formattedStartDate }})</span></label>
            <input v-model="form.starts_at" id="start-date" type="date" class="mt-2 bg-white border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 datepicker-input" placeholder="Select start date" onchange="filterCheck();">
        </div>
        <div class="relative">
            <label for="end-date" class="block text-sm font-medium text-gray-700">End Date <span v-if="formattedEndDate">({{ formattedEndDate }})</span></label>
            <input v-model="form.ends_at" id="end-date" type="date" class="mt-2 bg-white border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 datepicker-input" placeholder="Select end date" onchange="filterCheck();">
        </div>
    </div>
    <div class="w-full flex gap-2 justify-between mt-5">
        <div class="flex gap-2 w-full items-center">
            <v-select class="mt-1 block w-full bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" :options="managers" label="name" :reduce="managers => managers.id" v-model="form.manager" placeholder="Manager" />
            <v-select class="mt-1 block w-full bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" :options="suppliers" label="name" :reduce="suppliers => suppliers.id" v-model="form.supplier" placeholder="Supplier" />
            <v-select class="mt-1 block w-full bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" :options="clients" label="name" :reduce="clients => clients.id" v-model="form.client" placeholder="Clients" />
        </div>
    </div>
    <br />
    <div class="bg-white py-6 px-4 sm:p-6 shadow mb-6">
            <div class="flex flex-col mt-3 space-y-3">
                <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                        <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead class="bg-gray-50">
                                <tr>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Invoice Number
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Fixture
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Fixture Code
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Fixture Date
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Received Date
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Trading Entity
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Amount
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        GBP Value
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        % Invoiced
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="Object.values(invoices).length > 0" v-for="(invoice, index) in invoices.data" :key="index">
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <a :href="route('frontend.fixture.show' , invoice.fixture.id)">
                                            {{ invoice.invoice_number }}
                                        </a>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <a :href="route('frontend.fixture.show' , invoice.fixture.id)">
                                            {{ invoice.fixture.title }}
                                        </a>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <a :href="route('frontend.fixture.show' , invoice.fixture.id)">
                                            {{ invoice.fixture.reference }}
                                        </a>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <a :href="route('frontend.fixture.show' , invoice.fixture.id)">
                                            {{ moment(invoice.fixture.starts_at, 'YYYY-MM-DD').format('DD MMM YYYY') }}
                                        </a>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <a :href="route('frontend.fixture.show' , invoice.fixture.id)">
                                            {{ moment(invoice.received_at, 'YYYY-MM-DD').format('DD MMM YYYY') }}
                                        </a>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ invoice.fixture.client.trading_details ? invoice.fixture.client.trading_details.trading_name : '' }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        £{{ number_format(parseFloat(invoice.amount).toFixed(2)) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        £{{ number_format(parseFloat(invoice.gbp_amount).toFixed(2)) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ invoice.invoiced_amount }}%
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <Pagination :links="invoices.links"/>
        </div>
    </div>
</template>

<script>
    import Authenticated from "@/Layouts/Authenticated";
    import {Link, Head, useForm} from "@inertiajs/vue3";
    import Pagination from "@/Components/Pagination";
    import mapValues from "lodash/mapValues";
    import debounce from "lodash/debounce";
    import pickBy from "lodash/pickBy";
    import moment from "moment";

    export default {
        layout: Authenticated,
        components: {
            Head,
            Link,
            Pagination
        },
        props: ['invoices', 'clients', 'suppliers', 'managers', 'starts_at', 'ends_at', 'supplier_id', 'client_id', 'manager_id', 'trading_details'],
        data(props) {
            return {
                form: useForm({
                    starts_at: props.starts_at,
                    ends_at: props.ends_at,
                    supplier: props.supplier_id,
                    client: props.client_id,
                    manager: props.manager_id,
                    trading_entity: null
                })
            }
        },
        computed: {
            formattedStartDate() {
                if (this.form.starts_at) {
                    return moment(this.form.starts_at).format('DD MMM YYYY');
                }
                return '';
            },
            formattedEndDate() {
                if (this.form.ends_at) {
                    return moment(this.form.ends_at).format('DD MMM YYYY');
                }
                return '';
            }
        },
        created() {
            this.moment = moment
        },
        computed: {
            formattedStartDate() {
                if (this.form.starts_at) {
                    return moment(this.form.starts_at).format('DD MMM YYYY');
                }
                return '';
            },
            formattedEndDate() {
                if (this.form.ends_at) {
                    return moment(this.form.ends_at).format('DD MMM YYYY');
                }
                return '';
            }
        },
        methods: {
            reset() {
                this.form = mapValues(this.form, () => null)
            },
            number_format(nStr) {
                nStr += '';
                let x = nStr.split('.');
                let x1 = x[0];
                let x2 = x.length > 1 ? '.' + x[1] : '';
                let rgx = /(\d+)(\d{3})/;
                while (rgx.test(x1)) {
                    x1 = x1.replace(rgx, '$1' + ',' + '$2');
                }
                return x1 + x2;
            }
        },
        watch: {
            form: {
                deep: true,
                handler: debounce(function () {
                    this.$inertia.get(route('frontend.reports.invoice_status_report_suppliers'), pickBy(this.form), {preserveState: true, preserveScroll: true})
                }, 500),
            },
        }
    }

    // On load function check
    window.onload = function() {
        let managerInput     = document.getElementById('supplier_invoice_status_manager');
        let supplierInput    = document.getElementById('supplier_invoice_status_supplier');
        let clientInput      = document.getElementById('supplier_invoice_status_client');
        let percentPaidInput = document.getElementById('supplier_invoice_status_percent_paid');
        if (window.location.toString().includes("filter")) {
            managerInput.disabled     = false;
            supplierInput.disabled    = false;
            clientInput.disabled      = false;
            percentPaidInput.disabled = false;
        }
    };
</script>
